import { useSearchParams } from 'react-router-dom';
import {
  Building2,
  Calendar,
  CalendarClock,
  ChevronRight,
  Clock,
  Hash,
  LayoutGrid,
  Mail,
  Monitor,
  Phone,
  Play,
  Repeat,
  Shield,
  User,
  Wallet,
  XCircle,
} from 'lucide-react';
import type { CampaignOverview } from '@/features/campaigns/api/campaignDetailApi';
import { fmtCurrency, fmtDate, fmtDateTime } from '../campaignDetailPage.utils';
import { Card, PriceRow, StatCell } from '../CampaignDetailPagePrimitives';
import { CampaignCreativePreview } from '../sections/CampaignCreativePreview';
import {
  DisplayOrientationThumb,
  cleanScreenDisplayName,
} from '@/features/campaigns/components/ScheduleStep/sections/DisplayOrientationThumb';

type OverviewTabProps = {
  campaign: CampaignOverview;
  role: string;
  onSelectTab?: (tab: 'screens') => void;
};

export function OverviewTab({ campaign, role, onSelectTab }: OverviewTabProps) {
  const [, setSearchParams] = useSearchParams();
  const media = campaign.mediaIds || [];
  const pricing = campaign.pricing;
  const reasons = [
    { label: 'Rejection Reason', val: campaign.reject_reason },
    { label: 'Admin Escalation', val: campaign.admin_escalation_reason },
    { label: 'CCV Forward', val: campaign.ccv_forward_reason },
    { label: 'LCM Forward', val: campaign.lcm_forward_reason },
  ].filter((r) => r.val);

  const client =
    typeof campaign.agencyClientId === 'object' && campaign.agencyClientId !== null
      ? campaign.agencyClientId
      : typeof campaign.agencyClient === 'object' && campaign.agencyClient !== null
        ? campaign.agencyClient
        : null;

  const handleSeeMoreScreens = () => {
    if (onSelectTab) {
      onSelectTab('screens');
    } else {
      setSearchParams({ tab: 'screens' });
    }
  };

  const billboards = campaign.billboards || [];
  const visibleBillboards = billboards.slice(0, 5);
  const remainingScreensCount = billboards.length - 5;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
      <div className="flex flex-col gap-5 lg:col-span-8">
        <CampaignCreativePreview media={media} />

        <Card title="Campaign details" icon={<LayoutGrid className="h-4 w-4" />}>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            <StatCell
              icon={<Hash className="h-3.5 w-3.5" />}
              label="Order ID"
              value={campaign.orderId}
            />
            <StatCell
              icon={<Calendar className="h-3.5 w-3.5" />}
              label="Start date"
              value={fmtDate(campaign.startDate)}
            />
            <StatCell
              icon={<Calendar className="h-3.5 w-3.5" />}
              label="End date"
              value={fmtDate(campaign.endDate)}
            />
            <StatCell
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Duration"
              value={
                campaign.customDuration ? `${campaign.customDuration}s` : '—'
              }
            />
            <StatCell
              icon={<Play className="h-3.5 w-3.5" />}
              label="Booked slots"
              value={campaign.totalBookedSlots}
            />
            <StatCell
              icon={<Repeat className="h-3.5 w-3.5" />}
              label="Repetitions"
              value={campaign.totalRepetition}
            />
            <StatCell
              icon={<CalendarClock className="h-3.5 w-3.5" />}
              label="Submitted"
              value={fmtDateTime(campaign.createdAt)}
            />
            <StatCell
              icon={<User className="h-3.5 w-3.5" />}
              label="Submitted by"
              value={
                [
                  campaign.campaignSubmittedBy?.firstName,
                  campaign.campaignSubmittedBy?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ') ||
                campaign.campaignSubmittedBy?.username ||
                '—'
              }
            />
          </div>
        </Card>

        {client ? (
          <Card title="Client" icon={<Building2 className="h-4 w-4" />}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <StatCell
                icon={<User className="h-3.5 w-3.5" />}
                label="Name"
                value={
                  client.name ||
                  [client.firstName, client.lastName].filter(Boolean).join(' ') ||
                  '—'
                }
              />
              <StatCell
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Email"
                value={client?.email || '—'}
              />
              <StatCell
                icon={<Phone className="h-3.5 w-3.5" />}
                label="Mobile"
                value={client?.mobileNumber || '—'}
              />
              {client?.role ? (
                <StatCell
                  icon={<Shield className="h-3.5 w-3.5" />}
                  label="Role"
                  value={client?.role}
                />
              ) : null}
              {client?.approvedAt ? (
                <StatCell
                  icon={<CalendarClock className="h-3.5 w-3.5" />}
                  label="Approved"
                  value={fmtDateTime(client?.approvedAt)}
                />
              ) : null}
            </div>
          </Card>
        ) : null}

        {reasons.length > 0 ? (
          <Card
            title="Remarks"
            icon={<XCircle className="h-4 w-4 text-red-500" />}
          >
            <div className="flex flex-col gap-2.5">
              {reasons.map((r, i) => (
                <div
                  key={i}
                  className="rounded-[14px] border border-red-100 bg-red-50/70 px-4 py-3"
                >
                  <p className="m-0 text-[11px] font-semibold text-red-500">
                    {r.label}
                  </p>
                  <p className="m-0 mt-1 text-[13px] leading-relaxed text-red-700">
                    {Array.isArray(r.val) ? r.val.join(', ') : String(r.val)}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        ) : null}
      </div>

      <aside className="flex flex-col gap-5 lg:col-span-4">
        {pricing && role !== 'CLIENT' ? (
          <Card title="Payment" icon={<Wallet className="h-4 w-4" />}>
            <div className="flex flex-col gap-0.5">
              <PriceRow
                label="Base amount"
                value={fmtCurrency(pricing.baseAmount, pricing.currency)}
              />

              {campaign.taxDistribution?.length
                ? campaign.taxDistribution.map((t, index) => (
                    <PriceRow
                      key={`${t.role ?? 'tax'}-${index}`}
                      label={t.role ?? 'Tax'}
                      value={fmtCurrency(t.amount, pricing.currency)}
                    />
                  ))
                : null}

              {pricing.totalDiscountAmount ? (
                <PriceRow
                  label="Discount"
                  value={`−${fmtCurrency(pricing.totalDiscountAmount, pricing.currency)}`}
                  green
                />
              ) : null}

              <div className="my-2 border-t border-dashed border-black/[0.08]" />
              <PriceRow
                label="Total"
                value={fmtCurrency(pricing.totalAmount, pricing.currency)}
                bold
              />
            </div>
          </Card>
        ) : null}

        <Card
          title={`Screens · ${billboards.length}`}
          icon={<Monitor className="h-4 w-4" />}
          action={
            billboards.length > 0 ? (
              <button
                type="button"
                onClick={handleSeeMoreScreens}
                className="text-[12px] font-semibold text-[var(--dash-brand)] transition hover:opacity-80"
              >
                View all
              </button>
            ) : null
          }
        >
          <div className="flex flex-col gap-2">
            {visibleBillboards.map((b, i) => {
              const orientation = String(
                b.screenOrientation || b.screenType || '',
              ).toUpperCase();
              const name = cleanScreenDisplayName(b.screenName || 'Screen');
              return (
                <div
                  key={b.billboardId || i}
                  className="flex items-center gap-2.5 rounded-[14px] bg-[#F7F8FA] px-2.5 py-2 transition hover:bg-[#F2F4F7]"
                >
                  <DisplayOrientationThumb
                    orientation={orientation}
                    index={i + 1}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[13px] font-semibold tracking-[-0.015em] text-[var(--dash-ink)]">
                      {name}
                    </p>
                    <p className="m-0 truncate text-[11px] text-[var(--dash-muted)]">
                      {[b.address?.city, orientation === 'PORTRAIT' || orientation === 'LANDSCAPE' ? orientation.charAt(0) + orientation.slice(1).toLowerCase() : b.screenType]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                </div>
              );
            })}

            {!billboards.length ? (
              <p className="m-0 py-6 text-center text-xs text-[var(--dash-muted)]">
                No screens booked
              </p>
            ) : null}

            {remainingScreensCount > 0 ? (
              <button
                type="button"
                onClick={handleSeeMoreScreens}
                className="mt-1 flex w-full items-center justify-center gap-1 rounded-[12px] border border-black/[0.06] bg-white py-2.5 text-[12px] font-semibold text-[var(--dash-brand)] transition hover:bg-[#F7F8FA]"
              >
                <span>+{remainingScreensCount} more</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </Card>

        {campaign.isQrIncluded && campaign.qrInfo ? (
          <Card title="QR details" icon={<Hash className="h-4 w-4" />}>
            <div className="flex flex-col gap-0.5">
              <PriceRow label="Title" value={campaign.qrInfo.title} />
              <PriceRow label="WhatsApp" value={campaign.qrInfo.whatsAppNumber} />
              <PriceRow
                label="Coupon"
                value={campaign.qrInfo.couponCode || campaign.qrInfo.coupenCode}
              />
              <PriceRow label="Product link" value={campaign.qrInfo.productLink} />
            </div>
          </Card>
        ) : null}
      </aside>
    </div>
  );
}
